import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import API from '../api/axios';
import socket from '../socket';
import PropertyCard from '../components/PropertyCard';
import Pagination from '../components/Pagination';
import { Star, Building2, RefreshCw, Bell, MapPin } from 'lucide-react';
import { useScrollMemory } from '../hooks/useScrollMemory';
import { motion, AnimatePresence } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { smoothScrollToTop } from '../utils/scrollUtils';
import { BRAND } from '../config/brand';


const heroImage = '/images/hero_registry.avif';

// Global cache for instant navigation transitions
let cachedProperties = null;
let cachedTotalPages = 1;
let cachedHasMore = true;
let cachedClassifications = [];
let cachedPopularLocations = [];
let preloadingPromise = null;

export const preloadFeaturedData = async () => {
    if (cachedProperties || preloadingPromise) return preloadingPromise;

    preloadingPromise = (async () => {
        try {
            const [propRes, clsRes, locRes] = await Promise.all([
                API.get('/properties/featured?page=1&limit=6'),
                API.get('/classifications'),
                API.get('/properties/featured/locations')
            ]);
            cachedProperties = propRes.data.data;
            cachedTotalPages = propRes.data.totalPages;
            cachedHasMore = propRes.data.hasMore;
            cachedClassifications = clsRes.data;
            cachedPopularLocations = locRes.data;
        } catch (err) {
            console.error('Preload failed', err);
        } finally {
            preloadingPromise = null;
        }
    })();

    return preloadingPromise;
};

const FeaturedProperties = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const urlPage = Number(searchParams.get('page')) || 1;
    const classificationId = searchParams.get('classification');
    const locationParam = searchParams.get('location');

    const [properties, setProperties] = useState(() => {
        if (cachedProperties) return cachedProperties;
        if (typeof window !== 'undefined' && window.__INITIAL_DATA__ && window.__INITIAL_DATA__.pageType === 'featured') {
            return window.__INITIAL_DATA__.data || [];
        }
        return [];
    });
    const [loading, setLoading] = useState(false);
    const [listingMode, setListingMode] = useState('pagination');
    const [page, setPage] = useState(urlPage);
    const activePage = listingMode === 'pagination' ? urlPage : page;
    const [totalPages, setTotalPages] = useState(() => {
        if (cachedProperties) return cachedTotalPages;
        if (typeof window !== 'undefined' && window.__INITIAL_DATA__) {
            return window.__INITIAL_DATA__.totalPages || 1;
        }
        return 1;
    });
    const [hasMore, setHasMore] = useState(() => {
        if (cachedProperties) return cachedHasMore;
        if (typeof window !== 'undefined' && window.__INITIAL_DATA__) {
            return window.__INITIAL_DATA__.hasMore;
        }
        return true;
    });
    const [initialLoad, setInitialLoad] = useState(() => {
        if (cachedProperties) return false;
        if (typeof window !== 'undefined' && window.__INITIAL_DATA__ && window.__INITIAL_DATA__.pageType === 'featured') {
            return false;
        }
        return true;
    });
    const [newPropertyAlert, setNewPropertyAlert] = useState(null);
    const [highlightId, setHighlightId] = useState(null);
    const [search, setSearch] = useState(searchParams.get('search') || "");
    const [classifications, setClassifications] = useState(cachedClassifications);
    const isFirstRender = useRef(true);

    // Sync state with URL is no longer needed as we use activePage derived from urlPage

    // Update URL when page changes internally
    const handlePageChange = (newPage) => {
        // Reset scroll memory for new navigation to prevent jump-back
        sessionStorage.setItem('featured_properties_scroll_position', '0');
        if (listingMode === 'pagination') {
            const params = Object.fromEntries(searchParams.entries());
            params.page = newPage;
            setSearchParams(params);
        } else {
            setPage(newPage);
        }
    };

    const [popularLocations, setPopularLocations] = useState(cachedPopularLocations);
    const [locationSearch, setLocationSearch] = useState(locationParam || "");
    const [showLocationDropdown, setShowLocationDropdown] = useState(false);

    // Sync local states with URL for back/forward navigation
    useEffect(() => {
        setSearch(searchParams.get('search') || "");
        setLocationSearch(searchParams.get('location') || "");
    }, [searchParams]);

    // 2. Fetch Platform Configuration & Filters
    useEffect(() => {
        const fetchConfig = async () => {
            try {
                const res = await API.get('/settings/listing_mode');
                setListingMode(res.data.value || 'pagination');
            } catch (err) {
                console.error('Config fetch failed:', err);
            }
        };
        const fetchFilters = async () => {
            try {
                const [clsRes, locRes] = await Promise.all([
                    API.get('/classifications'),
                    API.get('/properties/featured/locations')
                ]);
                cachedClassifications = clsRes.data;
                cachedPopularLocations = locRes.data;
                setClassifications(clsRes.data);
                setPopularLocations(locRes.data);
            } catch (err) {
                console.error('Error fetching filters:', err);
            }
        };
        fetchConfig();
        fetchFilters();
    }, []);

    const handleFilterChange = (updates) => {
        const params = Object.fromEntries(searchParams.entries());
        
        // Always include current local states for inputs to keep URL in sync
        if (search) params.search = search; else delete params.search;
        if (locationSearch) params.location = locationSearch; else delete params.location;

        // Apply specific updates
        Object.keys(updates).forEach(key => {
            if (updates[key]) {
                params[key] = updates[key];
            } else {
                delete params[key];
            }
        });

        params.page = 1;
        setSearchParams(params);
        if (updates.location !== undefined) {
            setLocationSearch(updates.location || "");
        }
    };

    const clearFilters = () => {
        setSearchParams({ page: 1 });
        setLocationSearch("");
        setSearch("");
    };

    const getActiveFilterLabel = () => {
        const cls = classifications.find(c => c.id.toString() === classificationId);
        const loc = locationParam;

        if (cls && loc) return `${cls.name} in ${loc}`;
        if (cls) return `${cls.name} Registry`;
        if (loc) return `Properties in ${loc}`;
        return "Featured Properties";
    };

    // 1. Socket Listener for Real-Time Synchronization
    useEffect(() => {
        socket.on('new_featured_property', (newItem) => {
            // Automatically scroll to top for real‑time alerts
            smoothScrollToTop(1000);


            if (listingMode === 'infinite') {
                // In Infinite mode, inject directly at the top
                setProperties(prev => {
                    const exists = prev.find(p => p.id === newItem.id);
                    if (exists) return prev;
                    return [newItem, ...prev];
                });
            } else {
                // In Pagination mode, still inject if on page 1, or just show alert
                if (page === 1) {
                    setProperties(prev => {
                        const exists = prev.find(p => p.id === newItem.id);
                        if (exists) return prev;
                        return [newItem, ...prev];
                    });
                }
                setNewPropertyAlert(newItem);
                setTimeout(() => setNewPropertyAlert(null), 10000);
            }

            // Trigger highlight
            setHighlightId(newItem.id);
            setTimeout(() => setHighlightId(null), 3000);
        });

        socket.on('featured_property_added', (newItem) => {
            const propertyWithNew = { ...newItem, is_new: true };
            if (listingMode === 'infinite') {
                setProperties(prev => {
                    const exists = prev.find(p => p.id === newItem.id);
                    if (exists) return prev;
                    return [propertyWithNew, ...prev];
                });
            } else if (page === 1) {
                setProperties(prev => {
                    const exists = prev.find(p => p.id === newItem.id);
                    if (exists) return prev;
                    return [propertyWithNew, ...prev];
                });
            }
            setHighlightId(newItem.id);
            setTimeout(() => {
                setHighlightId(null);
                setProperties(prev => prev.map(p => p.id === newItem.id ? { ...p, is_new: false } : p));
            }, 10000);
        });

        return () => {
            socket.off('new_featured_property');
            socket.off('featured_property_added');
        };
    }, [listingMode, page]);

    // 3. Data Fetching Logic
    const fetchFeatured = async (isAppend = false) => {
        if (loading) return;
        setLoading(true);
        try {
            const minPrice = searchParams.get('minPrice') || '';
            const maxPrice = searchParams.get('maxPrice') || '';
            const res = await API.get(`/properties/featured?page=${activePage}&limit=6&search=${search}&classification=${classificationId || ''}&location=${locationParam || ''}&minPrice=${minPrice}&maxPrice=${maxPrice}`);
            const newData = res.data.data;

            if (isAppend) {
                setProperties(prev => {
                    const existingIds = new Set(prev.map(p => p.id));
                    const filtered = newData.filter(p => !existingIds.has(p.id));
                    const updated = [...prev, ...filtered];
                    cachedProperties = updated;
                    return updated;
                });
            } else {
                cachedProperties = newData;
                setProperties(newData);
            }

            cachedTotalPages = res.data.totalPages;
            cachedHasMore = res.data.hasMore;
            setTotalPages(res.data.totalPages);
            setHasMore(res.data.hasMore);
        } catch (err) {
            console.error('Registry sync failed:', err);
        } finally {
            setLoading(false);
            setInitialLoad(false);
        }
    };

    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            if (typeof window !== 'undefined' && window.__INITIAL_DATA__ && window.__INITIAL_DATA__.pageType === 'featured') {
                delete window.__INITIAL_DATA__; // Use it once then clear
                return;
            }
        }

        const delay = setTimeout(() => {
            fetchFeatured(listingMode === 'infinite' && activePage > 1);
        }, 300);

        return () => clearTimeout(delay);
    }, [activePage, listingMode, search, classificationId, locationParam]);

    // Reset pagination when search changes (classification and location already reset page in URL)
    useEffect(() => {
        if (search) {
            handlePageChange(1);
        }
    }, [search]);

    // 4. Infinite Scroll Intersection Observer
    const observer = useRef();
    const lastElementRef = useCallback(node => {
        if (loading || listingMode === 'pagination' || !hasMore) return;
        if (observer.current) observer.current.disconnect();

        observer.current = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting && hasMore && !loading) {
                setPage(prev => prev + 1);
            }
        }, { threshold: 0.1 });

        if (node) observer.current.observe(node);
    }, [loading, hasMore, listingMode]);

    return (
        <div className="pb-32 bg-[#FBFCFE]">
            <Helmet>
                <title>Properties for Sale & Rent in Sri Lanka | {BRAND.name} Registry</title>
                <meta name="description" content="Discover premium properties for sale and rent in Sri Lanka. Search luxury houses, apartments, and land in Colombo, Kandy, and Galle with ModernLand's elite discovery tools." />
                <meta name="keywords" content="houses for sale sri lanka, apartments for rent colombo, land for sale galle, real estate sri lanka, property marketplace, modernland listings" />
                <meta property="og:title" content={`Featured Properties in Sri Lanka | ${BRAND.name}`} />
                <meta property="og:description" content="Browse our elite registry of verified properties. Find your next home or investment with confidence." />
                <meta property="og:image" content={heroImage} />
                <meta property="og:type" content="website" />
                <meta property="og:url" content={window.location.href} />
                <link rel="canonical" href={window.location.href} />
            </Helmet>
            {/* Real-time Alert Toast */}
            {newPropertyAlert && (
                <div className="fixed bottom-12 left-1/2 z-[1000] bg-slate-900 text-white px-8 py-5 rounded-[2rem] shadow-3xl border border-white/10 flex items-center gap-6 transition-opacity duration-200">
                    <div className="w-12 h-12 bg-secondary rounded-2xl flex items-center justify-center text-white">
                        <Bell size={24} />
                    </div>
                    <div>
                        <p className="text-[10px] font-black uppercase tracking-widest text-secondary mb-1">New Institutional Listing</p>
                        <p className="text-sm font-bold truncate max-w-[200px]">{newPropertyAlert.title}</p>
                    </div>
                    <button
                        onClick={() => {
                            if (page === 1) {
                                fetchFeatured(false);
                            } else {
                                setPage(1);
                            }
                            setNewPropertyAlert(null);
                        }}
                        className="bg-white/10 hover:bg-white/20 px-5 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-opacity duration-200"
                    >
                        Refresh View
                    </button>
                </div>
            )}

            {/* Premium Hero Section */}
            <div className="relative h-[500px] flex items-center justify-center overflow-hidden mb-6">
                <div className="absolute inset-0">
                    <img
                        src={heroImage}
                        className="w-full h-full object-cover transform-gpu will-change-transform"
                        alt="Hero"
                        decoding="async"
                        loading="eager"
                    />
                    <div className="absolute inset-0 bg-gradient-to-b from-slate-900/70 via-slate-900/40 to-[#FBFCFE]"></div>
                </div>

                <div className="relative z-10 max-w-7xl mx-auto px-6 text-center">
                    <div className="inline-flex items-center gap-3 px-6 py-2 bg-white/10 backdrop-blur-md border border-white/20 text-white rounded-full text-[10px] font-black uppercase tracking-[0.4em] mb-8 shadow-2xl">
                        <Star size={14} className="text-amber-400" fill="currentColor" />
                        Elite Property Registry
                    </div>
                    <h1 className="text-3xl sm:text-5xl md:text-6xl font-black text-white mb-6 md:mb-8 tracking-tighter leading-[0.9] md:leading-[0.85] drop-shadow-2xl uppercase">
                        {getActiveFilterLabel()} <br />
                        <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-400 to-secondary-300 italic text-2xl sm:text-4xl md:text-6xl">Premium Selection</span>
                    </h1>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-4 md:px-6">
                {/* Combined Filter Bar */}
                <div className="relative z-50 -mt-20 mb-16">
                    <div className="bg-white rounded-[3rem] shadow-premium-lp p-4 border border-slate-100 flex flex-col lg:flex-row items-center gap-4">
                        {/* Search Input */}
                        <div className="flex-1 w-full relative">
                            <Star className="absolute left-6 top-1/2 -translate-y-1/2 text-slate-300" size={20} />
                            <input
                                type="text"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && handleFilterChange({ search })}
                                placeholder="Search title or keywords..."
                                className="w-full pl-16 pr-8 py-5 rounded-[2rem] bg-slate-50 border-none focus:ring-4 focus:ring-primary/5 transition-all text-sm font-bold text-primary"
                            />
                        </div>

                        {/* Classification Dropdown */}
                        <div className="w-full lg:w-64 relative">
                            <Building2 className="absolute left-6 top-1/2 -translate-y-1/2 text-slate-300 pointer-events-none" size={20} />
                            <select
                                value={classificationId || ""}
                                onChange={(e) => handleFilterChange({ classification: e.target.value })}
                                className="w-full pl-16 pr-10 py-5 rounded-[2rem] bg-slate-50 border-none focus:ring-4 focus:ring-primary/5 transition-all text-sm font-bold text-primary appearance-none cursor-pointer"
                            >
                                <option value="">All Types</option>
                                {classifications.map(c => (
                                    <option key={c.id} value={c.id}>{c.name}</option>
                                ))}
                            </select>
                            <div className="absolute right-6 top-1/2 -translate-y-1/2 text-slate-300 pointer-events-none">
                                <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
                            </div>
                        </div>

                        {/* Location Search Dropdown */}
                        <div className="w-full lg:w-72 relative">
                            <MapPin className="absolute left-6 top-1/2 -translate-y-1/2 text-slate-300 pointer-events-none" size={20} />
                            <input
                                type="text"
                                value={locationSearch}
                                onChange={(e) => {
                                    setLocationSearch(e.target.value);
                                    setShowLocationDropdown(true);
                                }}
                                onFocus={() => setShowLocationDropdown(true)}
                                onKeyDown={(e) => e.key === 'Enter' && handleFilterChange({ location: locationSearch })}
                                placeholder="Select Location..."
                                className="w-full pl-16 pr-8 py-5 rounded-[2rem] bg-slate-50 border-none focus:ring-4 focus:ring-primary/5 transition-all text-sm font-bold text-primary"
                            />

                            <AnimatePresence>
                                {showLocationDropdown && (
                                    <motion.div
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: 10 }}
                                        className="absolute top-full left-0 right-0 mt-4 bg-white rounded-3xl shadow-2xl border border-slate-100 max-h-64 overflow-y-auto z-[60] p-4"
                                    >
                                        <div className="flex flex-col gap-2">
                                            <button
                                                onClick={() => {
                                                    handleFilterChange({ location: "" });
                                                    setShowLocationDropdown(false);
                                                }}
                                                className="px-6 py-3 text-left rounded-xl hover:bg-slate-50 text-[10px] font-black uppercase tracking-widest text-slate-400"
                                            >
                                                Clear Location
                                            </button>

                                            {Array.isArray(popularLocations) && popularLocations.length > 0 ? (
                                                popularLocations
                                                    .filter(loc => loc.location && loc.location.toLowerCase().includes(locationSearch.toLowerCase()))
                                                    .map((loc, i) => (
                                                        <button
                                                            key={i}
                                                            onClick={() => {
                                                                handleFilterChange({ location: loc.location });
                                                                setShowLocationDropdown(false);
                                                            }}
                                                            className="px-6 py-4 text-left rounded-2xl hover:bg-primary hover:text-white transition-all group flex items-center justify-between"
                                                        >
                                                            <span className="text-sm font-bold capitalize">{loc.location}</span>
                                                            <span className="text-[10px] font-black opacity-40 group-hover:opacity-100">{loc.count} Assets</span>
                                                        </button>
                                                    ))
                                            ) : (
                                                <div className="px-6 py-8 text-center">
                                                    <p className="text-[10px] font-black text-slate-300 uppercase tracking-widest">No active locations found</p>
                                                </div>
                                            )}
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>

                        {/* Clear & Apply Actions */}
                        <div className="flex flex-col sm:flex-row items-center gap-3 w-full lg:w-auto">
                            {(classificationId || locationParam || search) && (
                                <button
                                    onClick={clearFilters}
                                    className="w-full sm:w-auto px-8 py-5 rounded-[2rem] text-[10px] font-black uppercase tracking-widest text-red-500 hover:bg-red-50 transition-all flex items-center justify-center gap-2"
                                >
                                    <RefreshCw size={14} />
                                    Reset
                                </button>
                            )}
                            <button
                                onClick={() => handleFilterChange({ search: search, location: locationSearch })} // Syncs local states to URL
                                className=" sm:flex-1 lg:flex-none px-10 py-5 bg-primary text-white rounded-[2rem] text-[11px] sm:text-[10px] font-black uppercase tracking-[0.2em] shadow-xl hover:bg-secondary transition-all active:scale-95"
                            >
                                Apply Filters
                            </button>
                        </div>
                    </div>
                </div>

                {/* Click outside to close location dropdown */}
                {showLocationDropdown && (
                    <div className="fixed inset-0 z-40" onClick={() => setShowLocationDropdown(false)}></div>
                )}
                {initialLoad && properties.length === 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12">
                        {[1, 2, 3, 4, 5, 6].map(i => (
                            <div key={i} className="bg-white h-[450px] rounded-[3.5rem] border border-slate-50 shadow-sm overflow-hidden">
                                <div className="h-64 bg-slate-100 animate-pulse"></div>
                                <div className="p-8 space-y-4">
                                    <div className="h-6 bg-slate-100 rounded-full w-3/4 animate-pulse"></div>
                                    <div className="h-4 bg-slate-50 rounded-full w-1/2 animate-pulse"></div>
                                    <div className="pt-8 flex justify-between">
                                        <div className="h-8 bg-slate-100 rounded-lg w-1/3 animate-pulse"></div>
                                        <div className="h-10 w-10 bg-slate-50 rounded-xl animate-pulse"></div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <>
                        <AnimatePresence mode="wait">
                            <motion.div
                                key={activePage}
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                transition={{ duration: 0.3, ease: "easeInOut" }}
                                className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-12 min-h-[800px] ${loading && !initialLoad ? 'opacity-50' : 'opacity-100'}`}
                            >
                                {properties.map((property, index) => {
                                    const isLast = listingMode === 'infinite' && properties.length === index + 1;
                                    return (
                                        <div
                                            key={property.id}
                                            ref={isLast ? lastElementRef : null}
                                            className={highlightId === property.id ? "bg-primary-100 transition-colors duration-200 rounded-[3.5rem]" : ""}
                                        >
                                            <PropertyCard property={property} />
                                        </div>
                                    );
                                })}
                            </motion.div>
                        </AnimatePresence>

                        {/* Navigation Controls */}
                        {listingMode === 'pagination' ? (
                            <div className="mt-2 flex justify-center">
                                <Pagination
                                    currentPage={activePage}
                                    totalPages={totalPages}
                                    onPageChange={handlePageChange}
                                />
                            </div>
                        ) : (
                            <>
                                {loading && (
                                    <div className="mt-20 flex flex-col items-center gap-4">
                                        <RefreshCw className="animate-spin text-secondary" size={32} />
                                        <span className="text-[10px] font-black text-slate-400 uppercase tracking-[0.3em]">Synchronizing Wave...</span>
                                    </div>
                                )}
                                {!hasMore && properties.length > 0 && (
                                    <div className="mt-24 text-center">
                                        <div className="inline-flex items-center gap-4 px-8 py-3 bg-slate-50 rounded-full border border-slate-100 text-[10px] font-black text-slate-400 uppercase tracking-[0.4em]">
                                            Boundary Reached
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                    </>
                )}

                {!loading && properties.length === 0 && !initialLoad && (
                    <div className="text-center py-32 bg-white rounded-[4rem] border border-slate-50 shadow-premium-lp">
                        <div className="w-24 h-24 bg-primary-50 text-primary-600 rounded-[2.5rem] flex items-center justify-center mx-auto mb-10 shadow-inner">
                            <Building2 size={40} />
                        </div>
                        <h3 className="text-3xl font-black text-slate-800 mb-4 tracking-tight uppercase">
                            {search ? `No results for "${search}"` : 'Empty Registry'}
                        </h3>
                    </div>
                )}
            </div>
        </div>
    );
};

export default FeaturedProperties;
